import { type ReactNode, useEffect, useMemo, useState } from 'react';
import {
  StytchB2BProvider,
  createStytchB2BClient,
  useStytchB2BClient,
  useStytchMember,
  useStytchMemberSession,
} from '@stytch/react/b2b';
import { useTranslation } from 'react-i18next';
import { setSessionSource } from '@/api/attachAuthInterceptors';
import { BootScreen } from '@/components/BootScreen';
import { SESSION_DURATION_MINUTES } from '@/config';
import { isDemoMode } from '@/lib/demoMode';
import { useDelayedPending } from '@/hooks/useDelayedPending';
import { loadPublicAuthConfig } from './bootConfigFetch';
import type { PublicAuthConfig } from './config';
import type { SessionTokenSource } from './sessionFetch';
import {
  DemoRuntimeAuthProvider,
  RuntimeAuthConfigContext,
  RuntimeAuthSdkContext,
  type RuntimeAuthSdk,
  type Workspace,
} from './runtimeAuthContext';

// Re-exported so this module stays the single import site every consumer
// already uses. The declarations live in the leaf because `.storybook/preview.ts`
// needs `DemoRuntimeAuthProvider` and nothing else in this file — see the header
// there.
export {
  DemoRuntimeAuthProvider,
  useRuntimeAuthConfig,
  useRuntimeAuthSdk,
} from './runtimeAuthContext';
export type {
  AuthSubject,
  RuntimeAuthSdk,
  Workspace,
  WorkspaceSwitchOutcome,
} from './runtimeAuthContext';

/**
 * The workspaces a Discovery listing says the member can enter, with the
 * session's own organization marked.
 *
 * Only `active_member` rows qualify. An invitation or an email-domain match is
 * something the person could JOIN, which the login-time chooser already offers
 * (`b2bDiscovery.ts` keeps both visible there); the switcher lists what they
 * already hold. Stytch has no "personal" flag — the personal workspace is an
 * ordinary organization here, one option among the rest.
 */
export function discoveredWorkspaces(
  discovered: ReadonlyArray<{
    organization: { organization_id: string; organization_name: string };
    membership: { type: string };
  }>,
  currentOrganizationId: string | undefined,
): Workspace[] {
  return discovered
    .filter((entry) => entry.membership.type === 'active_member')
    .map((entry) => ({
      id: entry.organization.organization_id,
      name: entry.organization.organization_name,
      isActive: entry.organization.organization_id === currentOrganizationId,
    }));
}

function B2BSdkBridge({
  config,
  children,
}: {
  config: PublicAuthConfig;
  children: ReactNode;
}) {
  const stytch = useStytchB2BClient();
  const { session, isInitialized: sessionInitialized } =
    useStytchMemberSession();
  const { member, isInitialized: memberInitialized } = useStytchMember();
  const [firstName = '', ...lastNameParts] =
    member?.name.trim().split(/\s+/) ?? [];
  const lastName = lastNameParts.join(' ');

  // The same object the transport was given in `getB2BClient`, so the SDK's
  // `getSessionJwt` / `refreshSession` keep one identity for the life of the
  // client: session and profile changes do not rotate them, and a refresh
  // started through the SDK and one started by the transport share one
  // in-flight slot (`sessionFetch` joins only a refresh with the same owner).
  const tokenSource = useMemo(() => b2bTokenSource(stytch), [stytch]);

  const value = useMemo<RuntimeAuthSdk>(() => {
    return {
      config,
      isInitialized: sessionInitialized && memberInitialized,
      subject:
        session && member
          ? {
              id: member.member_id,
              email: member.email_address,
              name: member.name || undefined,
              firstName: firstName || undefined,
              lastName: lastName || undefined,
            }
          : null,
      isEmailVerified: member?.email_address_verified ?? false,
      canSwitchWorkspace: true,
      getCurrentSubjectId: () => stytch.self.getSync()?.member_id,
      getSessionJwt: tokenSource.getSessionJwt,
      refreshSession: tokenSource.refreshSession,
      revokeSession: async () => {
        await stytch.session.revoke();
      },
      getAuthenticationToken: () =>
        stytch.parseAuthenticateUrl()?.token || undefined,
      listWorkspaces: async () => {
        // With a member session present the SDK lists against it, so this is
        // the same catalogue the login-time chooser would show this person.
        const response = await stytch.discovery.organizations.list();
        return discoveredWorkspaces(
          response.discovered_organizations,
          session?.organization_id,
        );
      },
      switchWorkspace: async (workspaceId) => {
        const response = await stytch.session.exchange({
          organization_id: workspaceId,
          session_duration_minutes: SESSION_DURATION_MINUTES,
        });
        return response.member_authenticated ? 'switched' : 'needs_sign_in';
      },
      updateProfile: async (nextFirstName, nextLastName) => {
        await stytch.self.update({
          name: `${nextFirstName} ${nextLastName}`.trim(),
        });
      },
    };
  }, [
    config,
    firstName,
    lastName,
    member,
    memberInitialized,
    session,
    sessionInitialized,
    stytch,
    tokenSource,
  ]);

  return (
    <RuntimeAuthSdkContext.Provider value={value}>
      {children}
    </RuntimeAuthSdkContext.Provider>
  );
}

/**
 * Cookie names the B2B client has used since the Consumer → B2B migration,
 * when the two projects shared an origin and Stytch's defaults
 * (`stytch_session`, `stytch_session_jwt`) let a Consumer tab overwrite the
 * B2B tokens. The Consumer project is gone (ENG-2371), but every live session
 * holds cookies under these names, so moving back to the defaults would sign
 * everyone out for nothing. Keep them.
 */
const B2B_COOKIE_OPTIONS = {
  opaqueTokenCookieName: 'stytch_session_b2b',
  jwtCookieName: 'stytch_session_jwt_b2b',
  istCookieName: 'stytch_intermediate_session_token_b2b',
} as const;

/**
 * Stytch clients are cached per public token at MODULE level, not per
 * component. A `useState` lazy initializer looks like it runs once, but
 * StrictMode double-invokes the render it lives in, so in dev it constructed
 * two clients and Stytch warned about "multiple copies of the Stytch client".
 * The token arrives at runtime from `GET /auth/config`, so the cache is keyed
 * by it rather than held in a single module constant, and every re-render
 * reuses one.
 */
type B2BClient = ReturnType<typeof createStytchB2BClient>;

const b2bClients = new Map<string, B2BClient>();

const b2bTokenSources = new WeakMap<B2BClient, SessionTokenSource>();

/**
 * One session source per Stytch client, built once. The transport holds it
 * (registered in `getB2BClient`) and the SDK bridge exposes its two functions,
 * so both sides refresh through the same `refreshSession` identity.
 */
function b2bTokenSource(client: B2BClient): SessionTokenSource {
  let source = b2bTokenSources.get(client);
  if (!source) {
    source = {
      getSessionJwt: () => client.session.getTokens()?.session_jwt || undefined,
      refreshSession: async () => {
        await client.session.authenticate({
          session_duration_minutes: SESSION_DURATION_MINUTES,
        });
      },
    };
    b2bTokenSources.set(client, source);
  }
  return source;
}

function getB2BClient(publicToken: string) {
  let client = b2bClients.get(publicToken);
  if (!client) {
    const created = createStytchB2BClient(publicToken, {
      cookieOptions: B2B_COOKIE_OPTIONS,
    });
    b2bClients.set(publicToken, created);
    // The API clients read the session through this client. Registered here,
    // in the render that mounts the provider, so it precedes every effect —
    // the router's first `router.load()` runs in a layout effect (ENG-2687).
    setSessionSource(b2bTokenSource(created));
    client = created;
  }
  return client;
}

function B2BAuthProvider({
  config,
  children,
}: {
  config: PublicAuthConfig;
  children: ReactNode;
}) {
  const client = getB2BClient(config.public_token);
  return (
    <StytchB2BProvider stytch={client}>
      <B2BSdkBridge config={config}>{children}</B2BSdkBridge>
    </StytchB2BProvider>
  );
}

type AuthConfigLoaderProps = {
  children: (config: PublicAuthConfig) => ReactNode;
  loader?: () => Promise<PublicAuthConfig>;
};

type AuthConfigState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; config: PublicAuthConfig };

export function AuthConfigLoader({
  children,
  loader = loadPublicAuthConfig,
}: AuthConfigLoaderProps) {
  const { t } = useTranslation();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<AuthConfigState>({ status: 'loading' });
  // Only the cold boot holds a blank frame. A retry is a click the user just
  // made: swallowing its feedback for 200ms — and entirely on a fast failure —
  // makes the button look broken. `attempt` only ever increments, so gating the
  // hook's input means a retry never arms a timer whose result is discarded.
  const delayedBootScreen = useDelayedPending(
    state.status === 'loading' && attempt === 0,
  );
  const showBootScreen =
    state.status === 'loading' && (attempt > 0 || delayedBootScreen);

  useEffect(() => {
    let active = true;
    void loader().then(
      (config) => {
        if (active) setState({ status: 'ready', config });
      },
      () => {
        if (active) setState({ status: 'error' });
      },
    );
    return () => {
      active = false;
    };
  }, [attempt, loader]);

  if (state.status === 'ready') return children(state.config);
  // Hold a blank frame for the first moments of a cold boot rather than
  // flashing a spinner the prefetched request usually outruns. Errors always
  // paint immediately — they are terminal and need the Retry button.
  if (state.status === 'loading') return showBootScreen ? <BootScreen /> : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="max-w-md text-center" role="alert">
        <h1 className="text-xl font-semibold text-foreground">
          {t('common.authUnavailableTitle')}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('common.authUnavailableBody')}
        </p>
        <button
          type="button"
          className="mt-6 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none"
          onClick={() => {
            setState({ status: 'loading' });
            setAttempt((current) => current + 1);
          }}
        >
          {t('common.retry')}
        </button>
      </div>
    </div>
  );
}

export function RuntimeAuthProvider({ children }: { children: ReactNode }) {
  if (isDemoMode()) {
    return <DemoRuntimeAuthProvider>{children}</DemoRuntimeAuthProvider>;
  }

  return (
    <AuthConfigLoader>
      {(config) => (
        <RuntimeAuthConfigContext.Provider value={config}>
          <B2BAuthProvider config={config}>{children}</B2BAuthProvider>
        </RuntimeAuthConfigContext.Provider>
      )}
    </AuthConfigLoader>
  );
}
